/**
 * Лучшая отработка возражения: цитата клиента и то, что ответил менеджер
 * (#846, решение владельца 27.09 — «цитата + 2–3 реплики менеджера»).
 *
 * Отрывок строится здесь, из стенограммы записи: `GET /records/:id/transcript`
 * сам проверяет доступ к записи (организация → проект → агент видит только
 * свои), и второй проверки в новом маршруте заводить не нужно. Стенограмма
 * берётся в сыром виде (`recordTranscriptRawOptions`): роли сегментов целы.
 * Анализ записи цитате неизвестен (`TRANSCRIPT_ANALYSIS_UNKNOWN`), поэтому кеш
 * с панелью записи она не делит — там ключ несёт анализ (#1851), и открытый
 * разговор скачает стенограмму ещё раз. Не `null`: под ним лежит то, что
 * панель взяла до анализа, — ASR-исходник, где цитаты анализа нет.
 *
 * Сверху — цитата из анализа, а не текст найденного сегмента: сегмент может
 * быть длинным монологом, а карточка показывает именно возражение.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { objectionAnswerExcerpt, type ExcerptSegment } from '@aica/core/voc';
import type { ObjectionStreamQuote } from '@aica/api-types/schemas';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import {
  recordTranscriptRawOptions,
  TRANSCRIPT_ANALYSIS_UNKNOWN,
} from '@/lib/query-options';
import type { SpeakerRoleMap } from '@/lib/speaker-roles';
import { speakerSide } from '@/lib/transcript-speaker';
import { cn } from '@/lib/utils';

/**
 * Segments with each side resolved the way the record's transcript resolves
 * it: the segment's own role, else the analysis' map for its ASR label
 * (`speakerSide`). The same quote then points at the same turn here and in
 * the transcript the "Open call" link leads to.
 */
function toSegments(raw: unknown, roles: SpeakerRoleMap | undefined): ExcerptSegment[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): ExcerptSegment[] => {
    if (!item || typeof item !== 'object') return [];
    const { speaker, role, text } = item as Record<string, unknown>;
    const label = typeof speaker === 'string' ? speaker : undefined;
    const own = typeof role === 'string' ? role : undefined;
    const mapped = label ? (speakerSide(label, roles) ?? undefined) : undefined;
    return [
      {
        speaker: label,
        role: own && own !== 'unknown' ? own : mapped,
        text: typeof text === 'string' ? text : undefined,
      },
    ];
  });
}

interface BestAnswerExcerptProps {
  answer: ObjectionStreamQuote;
  onOpen: (recordId: string, quote: string) => void;
}

export function BestAnswerExcerpt({ answer, onOpen }: BestAnswerExcerptProps) {
  const { t } = useTranslation('dashboard');
  const { apiClient, orgId } = useAuth();
  const transcript = useQuery(
    recordTranscriptRawOptions(
      apiClient,
      answer.recordId,
      orgId,
      TRANSCRIPT_ANALYSIS_UNKNOWN
    )
  );

  const excerpt = transcript.data
    ? objectionAnswerExcerpt(
        toSegments(
          transcript.data.diarization,
          answer.speakerRoles as SpeakerRoleMap | undefined
        ),
        answer.quote
      )
    : null;

  const message = (() => {
    if (transcript.isError) return t('objectionsPage.best.unavailable');
    if (!excerpt) return t('objectionsPage.best.notFound');
    if (excerpt.unattributed) return t('objectionsPage.best.unattributed');
    return t('objectionsPage.best.noReply');
  })();

  return (
    <div className="rounded-card border border-border bg-bg-surface p-3 text-xs">
      <p className="border-l-2 border-warning pl-2 italic text-fg">
        &ldquo;{answer.quote}&rdquo;
      </p>
      {transcript.isLoading ? (
        <Loader2
          className="mt-2 h-3.5 w-3.5 animate-spin text-fg-muted"
          aria-label={t('objectionsPage.best.loading')}
        />
      ) : excerpt && excerpt.following.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {excerpt.following.map((turn, index) => (
            <li
              key={index}
              className={cn(
                'pl-2',
                turn.speaker === 'client' ? 'text-fg-subtle italic' : 'text-fg'
              )}
            >
              <span className="mr-1 font-medium text-fg-muted">
                {t(`objectionsPage.best.speaker.${turn.speaker}`)}:
              </span>
              {turn.text}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-fg-muted">{message}</p>
      )}
      <div className="mt-2 flex items-center justify-between text-fg-muted">
        <span>
          {answer.agentName ?? t('objectionsPage.best.unknownAgent')}
          {answer.score !== null &&
            ` · ${t('objectionsPage.best.score', { score: answer.score })}`}
        </span>
        <button
          type="button"
          className="font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onOpen(answer.recordId, answer.quote)}
        >
          {t('objectionsPage.openCall')} →
        </button>
      </div>
    </div>
  );
}
